/**
 * Angle references (#162) — drafting angles measured from an existing line
 * instead of only from the horizontal.
 *
 * Angle snap has always rounded a segment's direction to the drafting table
 * (multiples of 30° and 45° from horizontal). That is the right lattice when
 * the drawing is square to the page and the wrong one the moment it isn't: a
 * shape drawn at 17° has no square, no 45° miter and no perpendicular that the
 * absolute table can express.
 *
 * A reference fixes that by rotating the same table onto an existing edge. The
 * reference is **the nearest visible construction edge to where the segment
 * starts** — one edge, not a set. Two reasons for exactly one:
 *
 *  - It is the one a maker means. You put the pen down on an edge and want to
 *    go off it at 45°; the edge you touched is the reference, and there is
 *    nothing to disambiguate.
 *  - Every reference doubles the candidate lattice. Two or three of them and
 *    the cursor is always within a whisker of *something*, which makes angle
 *    snap feel like it stopped working — the direction still lands on a clean
 *    angle, but not one the maker chose.
 *
 * The reference is taken from the segment's START, which is fixed for the whole
 * drag. Picking it from the moving cursor would let the lattice change under
 * the hand mid-stroke.
 */
import type { Point2 } from "./dragSnap";

/** The line a segment's angles are being measured from. */
export interface AngleReference {
  /** Direction of the reference edge at the point nearest the segment start,
   *  in code-space radians (y-down), normalized to `[0, π)`. A line has no
   *  head or tail, so a direction and its opposite are one reference — the
   *  candidate offsets cover the full circle either way. */
  dirRad: number;
  /** The point on the reference edge nearest the segment's start. The ghost
   *  ray is drawn through here so the maker can see which edge was picked. */
  at: Point2;
  /** Distance from the segment's start to `at`, in mm. Only used to choose
   *  between edges. */
  distanceMm: number;
}

/** Fold a direction onto `[0, π)`. */
export function normalizeReferenceDir(rad: number): number {
  const PI = Math.PI;
  let d = rad % PI;
  if (d < 0) d += PI;
  // `-1e-17 % π` lands on π itself after the correction above; fold it back.
  return d >= PI ? 0 : d;
}

/**
 * The nearest edge to `origin` within `radiusMm`, as an angle reference, or
 * null when nothing is close enough.
 *
 * `edges` are SVG path-data strings in the same coordinate space as `origin`.
 * They are parsed per call rather than cached: a construction drag changes the
 * lines as it goes, and this runs once per gesture start, not per frame.
 */
export function nearestAngleReference(
  origin: Point2,
  edges: readonly string[],
  radiusMm: number,
  scope: paper.PaperScope,
): AngleReference | null {
  const cursor = new scope.Point(origin.x, origin.y);
  let best: AngleReference | null = null;
  for (const pathData of edges) {
    let cp: paper.CompoundPath;
    try {
      cp = new scope.CompoundPath({ pathData, insert: false });
    } catch {
      continue;
    }
    const subs =
      cp.children && cp.children.length > 0
        ? (cp.children as paper.Path[])
        : [cp as unknown as paper.Path];
    for (const sub of subs) {
      const loc = sub.getNearestLocation(cursor);
      if (!loc) continue;
      const dist = loc.point.getDistance(cursor);
      if (dist > radiusMm) continue;
      if (best && dist >= best.distanceMm) continue;
      const tangent = loc.tangent;
      if (!tangent || (tangent.x === 0 && tangent.y === 0)) continue;
      best = {
        dirRad: normalizeReferenceDir(Math.atan2(tangent.y, tangent.x)),
        at: { x: loc.point.x, y: loc.point.y },
        distanceMm: dist,
      };
    }
    cp.remove();
  }
  return best;
}

/**
 * A point on the reference line, `lengthMm` away from `origin`, on whichever
 * side the segment is heading. Feeds the guide ray's ghost arm so the arc is
 * drawn between the reference and the segment rather than around the back.
 */
export function referenceGhostPoint(
  origin: Point2,
  dirRad: number,
  toward: Point2,
  lengthMm: number,
): Point2 {
  const ux = Math.cos(dirRad);
  const uy = Math.sin(dirRad);
  // Point the ghost the same way as the segment so the marked angle is the one
  // between them, not its 180° complement.
  const sign =
    (toward.x - origin.x) * ux + (toward.y - origin.y) * uy < 0 ? -1 : 1;
  return {
    x: origin.x + ux * lengthMm * sign,
    y: origin.y + uy * lengthMm * sign,
  };
}
